import React, { PropsWithRef, forwardRef } from 'react';
import { InputTextProps } from './types';
import { Input as JuicyInput } from '@vtbhome/juicyfront';
import { StyledFormGroup } from './input-text.styles';
import { validateValue } from './valid-value';
import { mergeRefs } from '@shared/lib/utils/merge-refs/merge-refs';
import { useInputAutocomplete } from '@shared/lib/hooks/use-input-autocomplete/use-input-autocomplete';
import { ErrorMessageInput } from '../error-message-input/error-message-input';

export const InputText = forwardRef(
    (
        { error, label, hint, mask, onChange, value, startValue, autoComplete, ...props }: PropsWithRef<InputTextProps>,
        ref: React.Ref<HTMLLabelElement | null>
    ) => {
        const inputRef = useInputAutocomplete(autoComplete);

        const saveInputCursorPosition = ({
            target,
            newValue,
            value,
        }: {
            target: HTMLInputElement;
            newValue: string;
            value?: string | null;
        }) => {
            const selectionStart = Number(target.selectionStart) - 1;
            if (newValue === value) {
                requestAnimationFrame(() => {
                    target.setSelectionRange(selectionStart, selectionStart);
                });
            }
        };

        const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            const eventValue = target.value;

            if (!onChange) {
                return;
            }
            if (mask) {
                const newValue = (eventValue || '').replace(mask, '');
                saveInputCursorPosition({ target, newValue, value });
                const newEvent = {
                    ...event,
                    target: { ...event.target, value: validateValue(newValue, startValue) },
                } as React.FormEvent<HTMLInputElement>;
                return onChange(newEvent);
            }
            const newEvent = {
                ...event,
                target: { ...event.target, value: validateValue(eventValue, startValue) },
            } as React.FormEvent<HTMLInputElement>;
            onChange(newEvent);
        };

        return (
            <StyledFormGroup
                label={label}
                required={props.required}
                $error={error}
                errorMessage={<ErrorMessageInput error={error} hint={hint} />}>
                <JuicyInput
                    ref={mergeRefs(ref, inputRef)}
                    invalid={Boolean(error)}
                    value={validateValue(value, startValue)}
                    onChange={handleChange}
                    {...props}
                />
            </StyledFormGroup>
        );
    }
);

InputText.displayName = 'InputText';
